// src/api/auth.js
/**
 * Public authentication API client.
 *
 * Talks to the regional API gateway's public auth routes (no bearer token
 * required — the user is signed out during these flows). The gateway proxies to
 * the backend user-service, which owns password reset end to end: it generates
 * the Firebase Auth reset link and sends the branded, localised email. This is
 * the same platform endpoint the patient web app uses, so the dashboard and web
 * app share one reset mechanism instead of the dashboard sending its own
 * Firebase client-SDK email.
 */

import api from "./axiosInstance";

export const PASSWORD_RESET_PATH = "/v1/app/auth/password-reset";

/**
 * Requests a password-reset email for the given address.
 *
 * Always resolves `{ ok: true }` regardless of whether the account exists
 * (anti-enumeration); errors are reserved for transport/configuration failures.
 *
 * @param {{ email: string, origin: string, language?: string }} payload -
 *   `origin` is the dashboard origin the reset link should return to (validated
 *   server-side against the Firebase project's authorized domains). `language`
 *   is the user's selected UI language so the email is composed in that
 *   language.
 * @returns {Promise<{ ok: boolean }>}
 * @throws {import('axios').AxiosError}
 */
export async function requestPasswordReset({ email, origin, language }) {
  const { data } = await api.post(PASSWORD_RESET_PATH, {
    email,
    origin,
    language,
  });
  return data;
}
